import React, { memo, useState } from "react";
import { Img } from "react-image";
import styled, { css } from "styled-components";
import { CircularProgress } from "@mui/material";
import { styled as muiStyled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Dialog from "@mui/material/Dialog";

import RpVideoPlayer from "@/components/RpVideoPlayer";
import { isYouTubeUrl, extractVideoIdFromUrl } from "@/helpers/utils";
import { getFormatDate } from "@/helpers/utils";
import DialogContent from "@mui/material/DialogContent";
import { STATIC_IMAGES } from "@/helpers/staticImages";
import TablePagination from "@/components/tables/TablePagination";
import type { ResourcePaginationHandler, ResourceVideo } from "../types";

const ReactImage = Img as unknown as React.ComponentType<{
  alt?: string;
  loader?: React.ReactNode;
  loaderContainer?: (_children: React.ReactNode) => React.ReactElement;
  src: string;
}>;

const StyledCard = muiStyled(Card)(() => ({
  borderRadius: "var(--radius-container)",
  height: "100%",
}));

const StyledCardActionArea = muiStyled(CardActionArea)(() => ({
  minHeight: "187px",
}));

const Overlay = muiStyled("div")(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
  background:
    "color-mix(in srgb, var(--palette-common-black) 27%, transparent)",
  color: "var(--neutral-white)",
  opacity: 1,
}));

const DateLine = muiStyled("div")(({ theme }) => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: "0",
    bottom: "-2px",
    width: "11%",
    height: "2px",
    backgroundColor: theme?.palette?.primary?.main,
  },
}));

const Title = muiStyled("div")(() => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  WebkitLineClamp: 3,
}));

interface ImgLoaderProps {
  $style?: string;
}

const ImgLoader = styled.div<ImgLoaderProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  transition: all 0.2s;
  ${(props) => css`
    ${props.$style}
  `}
`;

interface EducationalVideoSectionProps {
  educationalVideos?: ResourceVideo[];
  pageIndex: number;
  pageSize: number;
  handlePageChange: ResourcePaginationHandler;
  totalVideos: number;
  loader?: React.ReactNode;
  loaderBoxClass?: string;
  loaderBoxStyle?: React.CSSProperties;
}

function EducationalVideoSection({
  educationalVideos = [],
  pageIndex,
  pageSize,
  handlePageChange,
  totalVideos,
  loader = <CircularProgress size={20} color="primary" />,
  loaderBoxClass = "",
  loaderBoxStyle = {},
}: EducationalVideoSectionProps) {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleThumbnailClick = (url: string) => {
    setSelectedVideoUrl(url);
    setOpenDialog(true);
  };

  const totalCount = Math.ceil(totalVideos / pageSize);

  return (
    <div>
      <section className="rc-section text-center my-4">
        <div className="row my-5 justify-content-center position-relative align-items-stretch">
          {educationalVideos &&
            educationalVideos.map((u) => {
              if (!isYouTubeUrl(u.video_url)) return null;
              const videoId = extractVideoIdFromUrl(u.video_url);
              const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
              return (
                <div className="col-md-4 col-lg-3 mb-4" key={u.id}>
                  <StyledCard>
                    <StyledCardActionArea
                      onClick={() => handleThumbnailClick(u.video_url)}
                    >
                      <ReactImage
                        src={thumbnailUrl}
                        alt="thumbnail"
                        loader={loader}
                        loaderContainer={(children: React.ReactNode) => (
                          <ImgLoader
                            className={` ${loaderBoxClass}`}
                            style={loaderBoxStyle}
                          >
                            {children}
                          </ImgLoader>
                        )}
                      />
                      <Overlay>
                        <img
                          src={STATIC_IMAGES.youtubeIcon}
                          alt=""
                          className="img-fluid sg-youtube-img"
                        />
                      </Overlay>
                    </StyledCardActionArea>
                    <CardContent>
                      <DateLine className="d-flex justify-content-start rc-date mt-3">
                        <Typography component="span" className="small">
                          {getFormatDate(
                            u?.video_date ? u?.video_date : u?.createdAt
                          )}
                        </Typography>
                      </DateLine>
                      <Title className="rc-title pt-3">
                        <Typography component="h5" variant="body1">
                          {u.video_title}
                        </Typography>
                      </Title>
                    </CardContent>
                  </StyledCard>
                </div>
              );
            })}
        </div>
        <div className="row d-flex row d-flex justify-content-center paging">
          <TablePagination
            count={totalCount}
            page={pageIndex}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            className="float-right"
          />
        </div>
      </section>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        className="video-dialog"
      >
        <DialogContent className="p-0">
          <div style={{ overflow: "hidden" }}>
            <RpVideoPlayer url={selectedVideoUrl} light={null} playing={true} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default memo(EducationalVideoSection);
